import { EthnicityOptions, SIGNATURE_FONT_KEYS, } from '@govinity/shared-config';
import { z } from 'zod';
import { addressOptionalSchema } from '../addresses/index.js';
import { phoneOptionalSchema } from '../phones/index.js';
export var SeatApplicationStatus;
(function (SeatApplicationStatus) {
    SeatApplicationStatus["Draft"] = "draft";
    SeatApplicationStatus["Submitted"] = "submitted";
})(SeatApplicationStatus || (SeatApplicationStatus = {}));
export var MeetingBodySeatTermApplicationStatus;
(function (MeetingBodySeatTermApplicationStatus) {
    MeetingBodySeatTermApplicationStatus["Draft"] = "draft";
    MeetingBodySeatTermApplicationStatus["Pending"] = "pending";
    MeetingBodySeatTermApplicationStatus["Review"] = "review";
    MeetingBodySeatTermApplicationStatus["Nominated"] = "nominated";
    MeetingBodySeatTermApplicationStatus["Confirmed"] = "confirmed";
    MeetingBodySeatTermApplicationStatus["Declined"] = "declined";
    MeetingBodySeatTermApplicationStatus["Withdrawn"] = "withdrawn";
    MeetingBodySeatTermApplicationStatus["Archived"] = "archived";
})(MeetingBodySeatTermApplicationStatus || (MeetingBodySeatTermApplicationStatus = {}));
// Create validation and DTO type
export const seatApplicationCreateSchema = z.object({
    meetingBodySeatTermIds: z
        .array(z.number(), 'Meeting body seat term IDs are required')
        .min(1, 'At least one meeting body seat term ID is required'),
});
export const govSubmittedSeatApplicationCreateSchema = seatApplicationCreateSchema.extend({
    firstName: z.string('First name is required').trim().min(1, 'First name is required'),
    lastName: z.string('Last name is required').trim().min(1, 'Last name is required'),
});
// Custom field instance value schema for update operations
export const customFieldInstanceValueSchema = z.object({
    id: z.number().optional().nullable(), // Instance ID for updates (null for create)
    customFieldId: z.number().min(1, 'Custom field ID is required'), // Custom field ID for both create and update
    value: z.unknown(), // JSON value - can be string, number, boolean, array, or object
});
/** Custom field values nested inside each meeting body seat term application */
const meetingBodySeatTermApplicationCustomFieldsSchema = z.object({
    meetingBodySeatTermApplicationId: z.number().min(1),
    customFieldValues: z.array(customFieldInstanceValueSchema),
});
export const seatApplicationSubmitSchema = z.object({
    firstName: z.string('First name is required').min(1, 'First name is required'),
    lastName: z.string('Last name is required').min(1, 'Last name is required'),
    employer: z.string().trim().nullish(),
    occupation: z.string().trim().nullish(),
    ethnicity: z.enum(EthnicityOptions).nullish(),
    qualifications: z.string('Qualifications are required').min(1, 'Qualifications are required'),
    reason: z.string('Reason for interest is required').min(1, 'Reason for interest is required'),
    email: z
        .email('Invalid email address')
        .transform((val) => val?.toLowerCase())
        .nullish(),
    phone: phoneOptionalSchema.nullish(),
    address: addressOptionalSchema.nullish(),
    // Optional here because government staff submit on an applicant's behalf without ever
    // collecting a signature. Only an applicant's own submission requires one, which the public
    // submit route enforces.
    signatureName: z.string().trim().nullish(),
    signatureFont: z.enum(SIGNATURE_FONT_KEYS).nullish(),
    meetingBodySeatTermApplications: z.array(meetingBodySeatTermApplicationCustomFieldsSchema),
});
// Update validation and DTO type
export const seatApplicationUpdateSchema = seatApplicationSubmitSchema.partial().extend({
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    qualifications: z.string().nullish(),
    reason: z.string().nullish(),
});
// Add to term schema
export const seatApplicationAddToTermSchema = z.object({
    meetingBodySeatTermId: z.number('Meeting body seat term ID is required'),
});
export const seatApplicationNominateSchema = z.object({
    meetingBodySeatAssignmentId: z.number('Meeting body seat assignment ID is required'),
});
export const seatApplicationLinkAssignmentSchema = z.object({
    meetingBodySeatAssignmentId: z.number('Meeting body seat assignment ID is required'),
});
// Print schemas
/** Request body for printing one or more seat applications for a term */
export const seatApplicationPrintRequestSchema = z.object({
    seatApplicationIds: z.array(z.number()).min(1, 'At least one seat application ID is required'),
    hidePersonalInfo: z.boolean().optional().default(false),
    attachmentIds: z.array(z.number()).optional().default([]),
});
